/* W11-GOV — Evidence Graph navigable : document → phrase source → proposition
 * → décision humaine → objet du registre → obligations. La preuve, pas l'affirmation. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Network, Loader2, FileText, ShieldAlert, ListChecks, ArrowRight } from "lucide-react";

const STATUS_META = {
  accepted: { label: "Accepté", cls: "text-[#1D9E75] border-[#1D9E75]/40" },
  edited_accepted: { label: "Accepté (édité)", cls: "text-[#1D9E75] border-[#1D9E75]/40" },
  rejected: { label: "Rejeté", cls: "text-[#E24B4A] border-[#E24B4A]/40" },
  proposed: { label: "À valider", cls: "text-[#EF9F27] border-[#EF9F27]/40" },
};

export function EvidenceGraphPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/gov-intake/evidence-graph")
      .then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  }, []);
  if (!data) return <div className="flex justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="evidence-graph-panel">
      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
        <Network className="w-3.5 h-3.5 text-[#7F77DD]" />
        {data.counts.documents} document(s) · {data.counts.proposals} proposition(s) · {data.counts.obligations} obligation(s) — {data.note}
      </div>
      {data.graph.length === 0 && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="evidence-graph-empty">
          Aucune chaîne de preuve encore. Analysez un document dans l'Intake.
        </div>
      )}
      {data.graph.map((g, gi) => (
        <div key={g.document?.id || `orphan-${gi}`} className="noam-card p-3 space-y-2" data-testid={`evidence-doc-${g.document?.id || "orphan"}`}>
          <div className="flex items-center gap-2 text-xs">
            <FileText className="w-3.5 h-3.5 text-[#7F77DD]" />
            <span className="text-zinc-200 font-medium">{g.document ? `${g.document.label} (v${g.document.version})` : "Sources antérieures au corpus"}</span>
            <span className="text-[10px] text-zinc-500">{g.proposals.length} proposition(s)</span>
          </div>
          {g.proposals.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.proposed;
            return (
              <div key={p.id} className="ml-5 pl-3 border-l border-white/10 py-1 space-y-0.5" data-testid={`evidence-prop-${p.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  {p.kind === "risk" ? <ShieldAlert className="w-3 h-3 text-[#E24B4A]" /> : <ListChecks className="w-3 h-3 text-[#1D9E75]" />}
                  <span className="text-zinc-200">{p.title}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                  {p.materialized?.code && (
                    <Link to={`/app/risques/${p.materialized.id}`} className="text-[10px] text-[#378ADD] hover:underline flex items-center gap-0.5">
                      {p.materialized.code} <ArrowRight className="w-2.5 h-2.5" />
                    </Link>
                  )}
                </div>
                {p.excerpt && <div className="text-[10px] text-zinc-500 italic">« {p.excerpt.slice(0, 200)}{p.excerpt.length > 200 ? "…" : ""} »</div>}
                {p.reject_reason && <div className="text-[10px] text-[#E24B4A]">Écarté : {p.reject_reason} — {p.decided_by_email}</div>}
                {p.decided_by_email && p.status !== "rejected" && (
                  <div className="text-[10px] text-zinc-600">Validé par {p.decided_by_email} le {p.decided_at ? new Date(p.decided_at).toLocaleDateString("fr-CH") : "—"}</div>
                )}
                {(p.obligations || []).map((o) => (
                  <div key={o.id} className="text-[10px] text-zinc-400 ml-4" data-testid={`evidence-obligation-${o.id}`}>
                    ↳ {o.status === "open" ? "⚠" : "✓"} {o.title} — {o.owner_email || "sans responsable"}
                    {o.resolution ? ` · preuve : ${o.resolution.note.slice(0, 80)}` : ""}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
